import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import { apiClient } from './client';
import { ApiErrorKind } from './constants';
import { ApiError, isApiResponse } from './errors';

export { ApiError, isApiError } from './errors';

type QueryValue = string | number | boolean | readonly (string | number)[] | null | undefined;
/** Query-string values. `undefined`/`null` are dropped; arrays repeat the key (`?s=a&s=b`). */
export type Query = Record<string, QueryValue>;

export interface RequestOptions extends Omit<
  AxiosRequestConfig,
  'url' | 'params' | 'data' | 'baseURL'
> {
  query?: Query;
  /** JSON-serialised, or sent as-is for `FormData` / `Blob` (axios sets the Content-Type). */
  body?: unknown;
  /**
   * Unwrap the CMPS `{ success, message, data }` envelope (default `true`). Turn it off only for
   * endpoints that return a bare payload that happens to have a `success` field.
   */
  envelope?: boolean;
}

const unwrap = <T>(response: AxiosResponse<unknown>, envelope: boolean): T => {
  if (response.status === 204) return undefined as T;
  const payload = response.data;
  if (!envelope || !isApiResponse(payload)) return payload as T;
  if (!payload.success) {
    throw new ApiError({
      kind: ApiErrorKind.Business,
      status: response.status,
      message: payload.message ?? 'The request was not successful',
      body: payload,
    });
  }
  return payload.data as T;
};

/**
 * Typed request on the shared axios client: base URL, timeout, auth, errors → `ApiError`, and
 * the envelope is unwrapped here (never in modules). Pass the query's `signal` so TanStack Query
 * can cancel it.
 *
 * Replaces cmps-public-web's per-service `new Promise(...)` classes: a failed request always
 * rejects, so `isError` / `finally` always run.
 */
export const request = async <T>(
  path: string,
  { query, body, envelope = true, ...config }: RequestOptions = {},
): Promise<T> => {
  const response = await apiClient.request<unknown>({
    ...config,
    url: path,
    params: query,
    data: body,
  });
  return unwrap<T>(response, envelope);
};

/**
 * The full response (headers, status, Blob body) for downloads and other non-JSON endpoints.
 * Still goes through auth and error mapping.
 */
export const requestRaw = <T>(
  path: string,
  { query, body, envelope: _envelope, ...config }: RequestOptions = {},
): Promise<AxiosResponse<T>> => {
  return apiClient.request<T>({
    ...config,
    url: path,
    params: query,
    data: body,
  });
};

type VerbOptions = Omit<RequestOptions, 'method' | 'body'>;

/** Verb shorthands: `http.get<Permit>(permitsEndpoints.byId(id), { signal })`. */
export const http = {
  get: <T>(path: string, options?: VerbOptions) => request<T>(path, { ...options, method: 'GET' }),
  post: <T, TBody = unknown>(path: string, body?: TBody, options?: VerbOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T, TBody = unknown>(path: string, body?: TBody, options?: VerbOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  patch: <T, TBody = unknown>(path: string, body?: TBody, options?: VerbOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T = void>(path: string, options?: VerbOptions) =>
    request<T>(path, { ...options, method: 'DELETE' }),
};
